<template>
  <div id="app">
    <!-- Splash Screen -->
    <SplashScreen 
      :min-duration="1500"
      @complete="onSplashComplete"
    />
    
    <!-- Main Content -->
    <router-view />
    
    <!-- Cookie Consent Banner -->
    <CookieBanner />
    
    <!-- Toast para notificaciones -->
    <Toast position="top-right">
      <template #message="slotProps">
        <div class="toast-message">
          <p v-if="slotProps.message.summary" class="heading-6">
            {{ slotProps.message.summary }}
          </p>
          <p v-if="slotProps.message.detail" class="body-text-sm">
            {{ slotProps.message.detail }}
          </p>
        </div>
      </template>
    </Toast>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '@/config/theme'
import { useCookieConsent } from '@/composables/general/useCookieConsent'
import { useResourceOptimization } from '@/composables/general/useResourceOptimization'
import SplashScreen from '@/components/general/SplashScreen.vue'
import CookieBanner from '@/components/general/CookieBanner.vue'
import Toast from 'primevue/toast'
import homeHeroVideo from '@/assets/videos/hero/hero-video-2.mp4'
import servicesHeroVideo from '@/assets/videos/hero/hero-video.mp4'

/**
 * OPTIMIZACIONES DE CACHÉ IMPLEMENTADAS:
 * 
 * 1. Lazy Loading: Todas las imágenes y videos usan loading="lazy" y preload="metadata"
 * 2. Límites de Caché: Configurados en vite.config.mjs (2MB max, 50 imágenes)
 * 3. Videos Optimizados: Solo se reproducen cuando están visibles (IntersectionObserver)
 * 4. Limpieza Automática: Service Worker limpia caché antiguo automáticamente
 * 5. Code Splitting: Chunks separados para vendor e i18n
 * 6. Preload Inteligente: Solo cuando el usuario muestra interés
 * 
 * Para más información ver:
 * - @/config/cache.config.ts
 * - @/composables/general/useResourceOptimization.ts
 * - vite.config.mjs
 */

// Inicializar el tema una sola vez a nivel de app
const { initTheme } = useTheme()

// i18n locale (para ajustar el idioma del documento)
const { locale } = useI18n()

// Inicializar cookie consent
const { initConsent } = useCookieConsent()

// Inicializar optimización de recursos
const { preloadVideo } = useResourceOptimization()

const runWhenIdle = (task: () => void) => {
  if (typeof window === 'undefined') return

  const requestIdle = (window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback) => number
  }).requestIdleCallback

  if (requestIdle) {
    requestIdle(() => task())
    return
  }

  globalThis.setTimeout(task, 250)
}

// Splash screen complete handler
const onSplashComplete = () => {
  console.log('Splash screen complete')
}

onMounted(() => {
  // Inicializar tema del sistema
  initTheme()
  
  // Inicializar consentimiento de cookies
  initConsent()
  
  // Precargar video hero principal inmediatamente
  console.log('🎬 Precargando video hero principal...')
  preloadVideo(homeHeroVideo)
    .then(() => console.log('✅ Video hero principal listo para usar'))
    .catch(err => console.warn('⚠️ Error precargando video:', err))

  // Precargar video secundario en tiempo ocioso para mejorar cambio de ruta
  runWhenIdle(() => {
    preloadVideo(servicesHeroVideo)
      .then(() => console.log('✅ Video de services precargado en segundo plano'))
      .catch(err => console.warn('⚠️ Error precargando video secundario:', err))
  })
  
  // Initialize application
  console.log('Poseidonie App initialized')
})

// Sincronizar atributo lang del documento con el locale actual
watch(
  locale,
  (newLocale) => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', newLocale)
    }
  },
  { immediate: true }
)
</script>

<style>
/* Global styles are handled in tailwind.css */
#app {
  min-height: 100vh;
  overflow-x: hidden;
  position: relative;
}

.toast-message {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
</style>